/**
 * Password Reset Page
 */

import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { InputWithIcon } from '@shared/components/form'
import { usePasswordReset } from '@shared/lib/api'
import { useAuth } from '@shared/contexts'
import { passwordResetSchema, type PasswordResetFormData } from '@shared/lib/validators'
import { toast } from 'sonner'
import { Info } from 'lucide-react'

export function PasswordResetPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { refetchUser } = useAuth()
  const passwordResetMutation = usePasswordReset()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      password: '',
      repeatPassword: '',
      token: token || '',
    },
  })

  useEffect(() => {
    if (token) {
      setValue('token', token)
    }
  }, [token, setValue])

  const onSubmit = async (data: PasswordResetFormData) => {
    try {
      await passwordResetMutation.mutateAsync({
        token: data.token,
        newPassword: data.password,
      })

      // Backend sets httpOnly cookie, refetch user
      await refetchUser()

      toast.success('Contraseña actualizada correctamente')

      // Check for stored previous path
      const prevPath =
        localStorage.getItem('prevPathComment') || localStorage.getItem('prevPathContact')

      setTimeout(() => {
        void navigate(prevPath || '/')
      }, 2000)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al actualizar contraseña')
    }
  }

  // Show error message if no token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-auth bg-cover bg-center p-4">
        <Card className="w-full max-w-md p-6 shadow-sm">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="rounded-full bg-destructive/10 p-3">
              <Info className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <h2 className="text-lg font-semibold mb-2">Token de recuperación inválido</h2>
              <p className="text-sm text-muted-foreground">
                El enlace de recuperación es inválido o ha expirado.
              </p>
            </div>
            <Button onClick={() => void navigate('/')} className="w-full mt-2">
              Ir al Inicio
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-auth bg-cover bg-center p-4">
      <Card className="w-full max-w-md p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-center mb-6">Restablecer Contraseña</h1>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          {/* Password */}
          <div className="space-y-2">
            <InputWithIcon
              isPasswordToggle
              type="password"
              placeholder="Nueva Contraseña"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          {/* Repeat Password */}
          <div className="space-y-2">
            <InputWithIcon
              isPasswordToggle
              type="password"
              placeholder="Repetir Contraseña"
              {...register('repeatPassword')}
            />
            {errors.repeatPassword && (
              <p className="text-sm text-destructive">{errors.repeatPassword.message}</p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="bg-muted p-3 rounded-md flex gap-2">
            <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Sugerencias</p>
              <p>• Ingresa un mínimo de 8 caracteres</p>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={passwordResetMutation.isPending}
          >
            {passwordResetMutation.isPending ? 'Actualizando...' : 'Actualizar Contraseña'}
          </Button>
        </form>
      </Card>
    </div>
  )
}
